import React, { useEffect } from "react";

import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@apollo/client";
import { FormProvider, useForm, Controller } from "react-hook-form";
import { toast } from "react-toastify";

import {
  QUERY_CLIENT,
  QUERY_CLIENT_PREFERENCES,
} from "../../../../config/graphql/query";
import { UPDATE_CLIENT_PREFERENCES } from "../../../../config/graphql/mutation";

import { useClientId } from "../hooks/useClient";
import useHasRole from "../../../../lib/hooks/useHasRole";
import { useCurrentHasPlan } from "../../../../components/PlanBlock";
import Input from "../../../../components/Input";

type FieldValues = {
  preferences: {
    dataRetention: IDataRetentionPreferences;
  };
};

const DataRetentionSettings = React.memo(() => {
  const { t } = useTranslation(["dataRetention"]);

  const clientId = useClientId();

  const isAdmin = useHasRole(["ADMIN"]);
  const isClientAdmin = useHasRole(["CLIENT_ADMIN"]);

  const isFree = useCurrentHasPlan(["free"]);
  const disabled = isFree || !(isAdmin || isClientAdmin);

  const { data } = useQuery<{
    client: { preferences: IClientPreferences };
  }>(QUERY_CLIENT_PREFERENCES, {
    skip: !clientId,
    variables: { id: clientId },
    context: { headers: { ClientId: clientId } },
    fetchPolicy: "network-only",
  });

  const dataRetention = data?.client?.preferences?.dataRetention;

  const [onUpdate] = useMutation(UPDATE_CLIENT_PREFERENCES, {
    context: { headers: { ClientId: clientId } },
    refetchQueries: [{ query: QUERY_CLIENT, variables: { id: clientId } }],
  });

  const methods = useForm<FieldValues>({ shouldFocusError: false });

  useEffect(() => {
    if (data?.client) {
      methods.reset({
        preferences: {
          dataRetention: {
            accessLog: {
              enabled: !!dataRetention?.accessLog?.enabled,
              retentionMonths: dataRetention?.accessLog?.retentionMonths ?? 24,
            },
            credentials: {
              enabled: !!dataRetention?.credentials?.enabled,
              inactivityMonths:
                dataRetention?.credentials?.inactivityMonths ?? 12,
            },
          },
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const accessLogEnabled = methods.watch(
    "preferences.dataRetention.accessLog.enabled",
  );
  const retentionMonths = methods.watch(
    "preferences.dataRetention.accessLog.retentionMonths",
  );
  const credentialsEnabled = methods.watch(
    "preferences.dataRetention.credentials.enabled",
  );

  const onSubmit = async (variables: FieldValues) => {
    const values = variables.preferences?.dataRetention;

    try {
      await onUpdate({
        variables: {
          input: {
            id: clientId,
            preferences: {
              dataRetention: {
                accessLog: {
                  enabled: !!values?.accessLog?.enabled,
                  retentionMonths: parseInt(
                    String(values?.accessLog?.retentionMonths ?? 24),
                    10,
                  ),
                },
                credentials: {
                  enabled: !!values?.credentials?.enabled,
                  inactivityMonths: parseInt(
                    String(values?.credentials?.inactivityMonths ?? 12),
                    10,
                  ),
                },
              },
            },
          },
        },
      });
      toast.success<string>(t("dataRetention:toast.saved"));
    } catch (e: any) {
      toast.error<string>(e.message || t("dataRetention:toast.saveError"));
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <div className="row">
          <div className="col-xl-6">
            <div className="mb-4">
              <div>
                <h5>{t("dataRetention:title")}</h5>
              </div>
              <p className="mb-4 text-primary">{t("dataRetention:subtitle")}</p>
              <Controller
                name="preferences.dataRetention.accessLog.enabled"
                render={({
                  field: { value, name, onChange },
                  fieldState: { error },
                }) => (
                  <div className="form-group form-check mb-2">
                    <input
                      id={name}
                      type="checkbox"
                      className="form-check-input"
                      checked={!!value}
                      onChange={onChange}
                      disabled={disabled}
                    />
                    <label
                      className="form-check-label user-select-none"
                      htmlFor={name}
                    >
                      {t("dataRetention:input.label.enabled")}
                    </label>
                    {!!error && (
                      <div className="invalid-feedback">{error.message}</div>
                    )}
                  </div>
                )}
              />
              <Controller
                name="preferences.dataRetention.accessLog.retentionMonths"
                render={({ field: { value, name, onChange } }) => (
                  <div className="d-flex align-items-center mb-1">
                    <label
                      className="form-check-label user-select-none mr-2 text-nowrap"
                      htmlFor={name}
                    >
                      {t("dataRetention:input.label.retentionMonths")}
                    </label>
                    <input
                      id={name}
                      type="range"
                      min={1}
                      max={24}
                      step={1}
                      value={value ?? 24}
                      onChange={onChange}
                      disabled={disabled || !accessLogEnabled}
                      style={{ maxWidth: "14rem" }}
                    />
                    <span className="ml-2 text-nowrap">
                      {t("dataRetention:input.label.months", {
                        months: parseInt(String(retentionMonths ?? 24), 10),
                      })}
                    </span>
                  </div>
                )}
              />
              <small className="text-muted d-block">
                {t("dataRetention:description.accessLog")}
              </small>
            </div>
            <div className="mb-4">
              <Controller
                name="preferences.dataRetention.credentials.enabled"
                render={({
                  field: { value, name, onChange },
                  fieldState: { error },
                }) => (
                  <div className="form-group form-check mb-2">
                    <input
                      id={name}
                      type="checkbox"
                      className="form-check-input"
                      checked={!!value}
                      onChange={onChange}
                      disabled={disabled}
                    />
                    <label
                      className="form-check-label user-select-none"
                      htmlFor={name}
                    >
                      {t("dataRetention:input.label.credentialsEnabled")}
                    </label>
                    {!!error && (
                      <div className="invalid-feedback">{error.message}</div>
                    )}
                  </div>
                )}
              />
              <div className="d-flex align-items-center mb-1">
                <Input
                  name="preferences.dataRetention.credentials.inactivityMonths"
                  type="number"
                  disabled={disabled || !credentialsEnabled}
                  min={1}
                  step={1}
                  style={{
                    width: "5rem",
                  }}
                />
                <span className="ml-1">
                  {t("dataRetention:input.label.credentialsInactivityMonths")}
                </span>
              </div>
              <small className="text-muted d-block">
                {t("dataRetention:description.credentials")}
              </small>
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-12">
            <button
              type="submit"
              className="btn btn-primary mt-2 mb-3"
              disabled={methods.formState.isSubmitting || disabled}
            >
              {t("dataRetention:button.submit")}
            </button>
          </div>
        </div>
      </form>
    </FormProvider>
  );
});

export default DataRetentionSettings;
